import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';
import { ChevronUp, ChevronDown, Lock } from 'lucide-react';
import { CFormSwitch } from "@coreui/react";

// Deliberately the Picks stylesheet rather than a copy. This IS the Picks
// table — same columns, same controls — and two stylesheets would drift the
// moment one of them is touched.
import picksStyles from '../picksSections/PicksSections.module.scss';
import ownStyles from './SummarySections.module.scss';

import Spinner from '../../components/spinner';
import DailyLimitInput, { MIN_DAILY_LIMIT } from '../picksSections/dailyLimitInput';
import { useLanguage } from '../../context/languageContext';

// One lookup over both stylesheets, so the markup reads the same as the Picks
// page's.
const styles = { ...picksStyles, ...ownStyles };

const API = process.env.REACT_APP_API_ROOT;

/**
 * The running order of the app's Summary tab.
 *
 * Until now this lived in the Flutter binary: which rails appear, what they are
 * called, where they sit. This page is that layout.
 *
 * Two rails — For You and Journeys — are computed per reader from their own
 * genres, so there is no shared list to curate and no count to show. They are
 * still listed, and can still be moved and hidden, because WHERE a personalised
 * rail sits is a layout decision like any other. Hiding them from the table
 * would only make the page disagree with the app.
 */

const REFRESH_OPTIONS = [1, 2, 3, 4, 6, 8, 12, 24];
const refreshLabel = (hours) => (hours === 24 ? "Daily" : `Every ${hours}h`);

/**
 * What Max in App falls back to when blank, per source — the backend's
 * MOST_RECOMMENDED_SIZE and LATEST_SIZE. Anything not listed shows all.
 */
const DEFAULT_DAILY_LIMIT = { mostRecommended: 20, latest: 20 };

/** What produces this rail's cards, in the admin's words. */
const SOURCE_LABELS = {
    manual: "Hand-Picked",
    forYou: "Automatic",
    journeys: "Automatic",
    latest: "Automatic",
    trending: "Automatic",
    mostRecommended: "Automatic",
};

function SummarySections() {
    const { adminInfo, selectedLang } = useLanguage();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [sections, setSections] = useState([]);
    const [isSavingOrder, setIsSavingOrder] = useState(false);
    const [pendingStatusIds, setPendingStatusIds] = useState([]);
    const [pendingCuratedIds, setPendingCuratedIds] = useState([]);

    const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };

    const fetchAllSections = async () => {
        setLoading(true);
        try {
            const { data: response } = await axios.get(
                `${API}/api/admin/summary/sections/getall`, { headers }
            );
            if (response.status) {
                setSections(response?.data || []);
            } else {
                toast.error(response.message || "Could not load the Summary sections.");
            }
        } catch (error) {
            console.error("fetchAllSections catch error::", error);
            toast.error("Could not load the Summary sections.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (adminInfo && adminInfo.token) fetchAllSections();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo]);

    // Titles are per-language; show the one for the language being edited and
    // fall back to English so a section is never nameless in the table.
    const titleFor = (section) => {
        const translations = section.translations || [];
        const match = translations.find((t) => t.langCode === selectedLang)
            || translations.find((t) => t.langCode === "en")
            || translations[0];
        return match?.title || section.key;
    };

    /**
     * Moves a section and persists the new order. The list moves first so it
     * feels instant, and is refetched if the request fails — refetched rather
     * than rolled back, because a snapshot would also undo any toggle made
     * since.
     */
    const handleMoveSection = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= sections.length || isSavingOrder) return;

        const reordered = [...sections];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        setSections(reordered);
        setIsSavingOrder(true);

        try {
            const { data: response } = await axios.put(
                `${API}/api/admin/summary/sections/reorder`,
                { orderedSectionIds: reordered.map((s) => s.sectionId) },
                { headers }
            );
            if (response.status) {
                toast.success("Section order has been updated.");
            } else {
                toast.error("Could not update the section order.");
                fetchAllSections();
            }
        } catch (error) {
            console.error("handleMoveSection catch error::", error);
            toast.error("Could not update the section order.");
            fetchAllSections();
        } finally {
            setIsSavingOrder(false);
        }
    };

    const handleChangeSectionStatus = async (id, newStatus, title) => {
        // Without a guard, rapid toggling fires overlapping PUTs whose
        // completion order isn't guaranteed, so the UI can end up disagreeing
        // with the server.
        if (pendingStatusIds.includes(id)) return;
        setPendingStatusIds((prev) => [...prev, id]);

        setSections((prev) => prev.map((s) => s.sectionId === id ? { ...s, isActive: newStatus } : s));
        const revert = () =>
            setSections((prev) => prev.map((s) => s.sectionId === id ? { ...s, isActive: !newStatus } : s));

        try {
            const { data: response } = await axios.put(
                `${API}/api/admin/summary/sections/update/status/${id}`,
                { isActive: newStatus },
                { headers }
            );
            if (response.status) {
                toast.success(newStatus ? `${title} is now visible in the app.` : `${title} is now hidden in the app.`);
            } else {
                revert();
                toast.error(response.message || `Could not update ${title}.`);
            }
        } catch (error) {
            console.error("handleChangeSectionStatus catch error::", error);
            revert();
            toast.error(`Could not update ${title}.`);
        } finally {
            setPendingStatusIds((prev) => prev.filter((p) => p !== id));
        }
    };

    const handleChangeSectionCurated = async (id, curated, title) => {
        if (pendingCuratedIds.includes(id)) return;
        setPendingCuratedIds((prev) => [...prev, id]);

        setSections((prev) => prev.map((s) => s.sectionId === id ? { ...s, curated } : s));
        const revert = () =>
            setSections((prev) => prev.map((s) => s.sectionId === id ? { ...s, curated: !curated } : s));

        try {
            const { data: response } = await axios.put(
                `${API}/api/admin/summary/sections/update/curated/${id}`,
                { curated },
                { headers }
            );
            if (response.status) {
                toast.success(curated
                    ? `${title} now uses your order.`
                    : `${title} is now shuffled for each reader.`);
            } else {
                revert();
                toast.error(response.message || `Could not update ${title}.`);
            }
        } catch (error) {
            console.error("handleChangeSectionCurated catch error::", error);
            revert();
            toast.error(error?.response?.data?.error || `Could not update ${title}.`);
        } finally {
            setPendingCuratedIds((prev) => prev.filter((p) => p !== id));
        }
    };

    /** Saves Max in App (`itemLimit`) or the refresh rate (`refreshHours`). */
    // `localOnly` is merged into the row but not sent — a field the server
    // derives from what was saved (For You's Total Count is its size).
    const handleSaveSetting = async (section, values, localOnly = {}) => {
        const title = titleFor(section);
        const previous = { ...section };
        setSections((prev) => prev.map((s) => s.sectionId === section.sectionId ? { ...s, ...values, ...localOnly } : s));

        try {
            const { data: response } = await axios.put(
                `${API}/api/admin/summary/sections/update/${section.sectionId}`,
                { values },
                { headers }
            );
            if (response.status) {
                toast.success(`${title} updated.`);
            } else {
                setSections((prev) => prev.map((s) => s.sectionId === section.sectionId ? previous : s));
                toast.error(response.message || `Could not update ${title}.`);
            }
        } catch (error) {
            console.error("handleSaveSetting catch error::", error);
            setSections((prev) => prev.map((s) => s.sectionId === section.sectionId ? previous : s));
            toast.error(error?.response?.data?.error || `Could not update ${title}.`);
        }
    };

    const isAutomatic = (section) => (section.source ?? "manual") !== "manual";

    return (
        <React.Fragment>
            <div className="heading">
                <div>
                    <h4 className="mb-0">Summary Sections{sections.length > 0 ? ` (${sections.length})` : ""}</h4>
                    <small className="text-muted">
                        The sections of the app's Summary tab, in the order they appear. Reorder with the
                        arrows and show or hide with the toggle. The app shows 12 cards on each rail and the
                        rest under See All. Max in App caps how many a reader can reach in all, rail and
                        See All together (blank = all, at least {MIN_DAILY_LIMIT}). Curated on serves your order to everyone; off, each reader
                        gets their own shuffle, which turns over at the refresh rate. For You and
                        Journeys are built from each reader's own genres, so only their position and
                        visibility can be changed.
                    </small>
                </div>
            </div>

            {loading ? (
                <Spinner />
            ) : (
                <div className={`${styles.container} table-responsive mt-1`}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.order}>Order</th>
                                <th>Section</th>
                                <th>Type</th>
                                <th>Automatic / Hand-Picked</th>
                                <th>Total Count</th>
                                <th>Max in App</th>
                                <th>Refresh Rate</th>
                                <th>Visible</th>
                                <th>Curated</th>
                                <th className={styles.action}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sections.map((section, index) => {
                                const locked = section.personalised === true;
                                const auto = isAutomatic(section);
                                const banner = section.layout === "banner";
                                // Curated means "serve MY order", so it only applies where there
                                // IS one: a hand-picked rail. A banner shows a single card, and
                                // every automatic source resolves its own members AND their
                                // order — the switch was stored there and read by nothing.
                                const curatable = !banner && !locked && !auto;

                                return (
                                    <tr key={section.sectionId}>
                                        <td className={styles.order}>
                                            <div className="d-flex align-items-center gap-1">
                                                <span className={styles.position}>{index + 1}</span>
                                                <button
                                                    type="button"
                                                    className={styles.moveBtn}
                                                    title="Move up"
                                                    disabled={index === 0 || isSavingOrder}
                                                    onClick={() => handleMoveSection(index, -1)}
                                                >
                                                    <ChevronUp size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className={styles.moveBtn}
                                                    title="Move down"
                                                    disabled={index === sections.length - 1 || isSavingOrder}
                                                    onClick={() => handleMoveSection(index, 1)}
                                                >
                                                    <ChevronDown size={16} />
                                                </button>
                                            </div>
                                        </td>

                                        <td className={styles.sectionName}>
                                            <span className="d-inline-flex align-items-center gap-2">
                                                {titleFor(section)}
                                                {locked && (
                                                    <Lock
                                                        size={13}
                                                        className="text-medium-emphasis"
                                                        title={section.lockedReason || "Customised to each reader."}
                                                    />
                                                )}
                                            </span>
                                            {locked && (
                                                <div className={styles.lockedNote}>{section.lockedReason}</div>
                                            )}
                                        </td>

                                        <td className={styles.layout}>{banner ? "Banner" : "Rail"}</td>

                                        <td>{SOURCE_LABELS[section.source] || "Hand-Picked"}</td>

                                        <td className={styles.itemCount}>
                                            {/* null, not 0 — an automatic rail has no count until it
                                                runs, and a zero reads as "empty". */}
                                            {section.itemCount === null || section.itemCount === undefined
                                                ? <span className="text-medium-emphasis">—</span>
                                                : section.itemCount}
                                        </td>

                                        <td className={styles.itemCount}>
                                            {/* For You's cap IS its size: every reader is dealt
                                                "Books for each reader", so the cell edits that
                                                (the same number as its editor, and its Total
                                                Count). Not on the banner (one card) or
                                                Journeys, which is the reader's own. */}
                                            {section.source === "forYou" ? (
                                                <DailyLimitInput
                                                    value={section.booksPerReader}
                                                    placeholder="15"
                                                    min={1}
                                                    allowBlank={false}
                                                    invalidMessage="Books for each reader must be a whole number, 1 or more."
                                                    title="Books for each reader — dealt equally across the genres each reader chose."
                                                    onCommit={(booksPerReader) =>
                                                        handleSaveSetting(section, { booksPerReader }, { itemCount: booksPerReader })}
                                                />
                                            ) : banner || locked ? (
                                                <span
                                                    className="text-medium-emphasis"
                                                    title={banner
                                                        ? "The banner shows one card."
                                                        : "Built from each reader's own genres."}
                                                >—</span>
                                            ) : (
                                                <DailyLimitInput
                                                    value={section.itemLimit}
                                                    placeholder={String(DEFAULT_DAILY_LIMIT[section.source] ?? "All")}
                                                    title={`How many a reader can reach in one refresh period, rail and See All together. Blank = ${DEFAULT_DAILY_LIMIT[section.source] ?? "all"}; at least ${MIN_DAILY_LIMIT}.`}
                                                    onCommit={(itemLimit) => handleSaveSetting(section, { itemLimit })}
                                                />
                                            )}
                                        </td>

                                        <td>
                                            {/* Every rail re-rolls its order on some period — the
                                                personalised ones per reader, the automatic ones for
                                                everybody at once — and this is that period. The
                                                banner is the exception: it shows one card and turns
                                                over on its schedule, not on a clock. */}
                                            {banner ? (
                                                <span
                                                    className="text-medium-emphasis"
                                                    title="The banner shows one card and turns over on its schedule."
                                                >—</span>
                                            ) : (
                                                <select
                                                    className={styles.refreshSelect}
                                                    // Honest about the one case where it waits: a
                                                    // curated hand-picked rail serves the admin's
                                                    // order, so there is nothing to re-roll until
                                                    // Curated goes off.
                                                    title={
                                                        !auto && !locked && section.curated !== false
                                                            ? "Saved, and applies when Curated is switched off — a fixed order has nothing to re-roll."
                                                            : "How often this rail re-rolls its order."
                                                    }
                                                    value={section.refreshHours ?? 6}
                                                    onChange={(e) =>
                                                        handleSaveSetting(section, { refreshHours: Number(e.target.value) })}
                                                >
                                                    {REFRESH_OPTIONS.map((h) => (
                                                        <option key={h} value={h}>{refreshLabel(h)}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </td>

                                        <td className={styles.status}>
                                            {/* Live even on a locked rail: whether a personalised
                                                section appears at all is still the admin's call. */}
                                            <CFormSwitch
                                                size="lg"
                                                variant="3d"
                                                color="primary"
                                                style={{ paddingLeft: 0, width: "42px", height: "22px", cursor: "pointer" }}
                                                checked={section.isActive === true}
                                                disabled={pendingStatusIds.includes(section.sectionId)}
                                                onChange={(e) =>
                                                    handleChangeSectionStatus(section.sectionId, e.target.checked, titleFor(section))}
                                            />
                                        </td>

                                        <td className={styles.status}>
                                            {curatable ? (
                                                <CFormSwitch
                                                    size="lg"
                                                    variant="3d"
                                                    color="primary"
                                                    style={{ paddingLeft: 0, width: "42px", height: "22px", cursor: "pointer" }}
                                                    title={section.curated === false
                                                        ? "Shuffled for each reader — switch on to use your order"
                                                        : "Using your order — switch off to shuffle per reader"}
                                                    checked={section.curated !== false}
                                                    disabled={pendingCuratedIds.includes(section.sectionId)}
                                                    onChange={(e) =>
                                                        handleChangeSectionCurated(section.sectionId, e.target.checked, titleFor(section))}
                                                />
                                            ) : (
                                                <span
                                                    className="text-medium-emphasis"
                                                    title={locked ? section.lockedReason : "A banner shows one card, so there is no order to shuffle."}
                                                >—</span>
                                            )}
                                        </td>

                                        <td className={styles.action}>
                                            <div className="d-inline-flex align-items-center gap-1">
                                                {/* Every row is editable, because every row has a
                                                    title. The banner has more than that — its
                                                    book, colour and schedule — so it goes to its
                                                    own editor; everything else renames in place.
                                                    An automatic rail with no Action at all read
                                                    as "nothing here is editable", when the one
                                                    editable thing simply had no way in. */}
                                                {banner ? (
                                                    <div title="Edit the featured card" onClick={() => navigate("/featured-summary")}>
                                                        <FaEdit size={20} />
                                                    </div>
                                                ) : (
                                                    <div
                                                        title={locked ? "Rename this rail" : "Edit this section"}
                                                        onClick={() => navigate(`/summary-sections/${section.sectionId}/edit`)}
                                                    >
                                                        <FaEdit size={20} />
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

        </React.Fragment>
    );
}

export default SummarySections;
